import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const TravelBudget = () => {
  // State variables for travel budget calculation
  const [transportation, setTransportation] = useState(0);
  const [accommodation, setAccommodation] = useState(0);
  const [food, setFood] = useState(0);
  const [activities, setActivities] = useState(0);
  const [totalBudget, setTotalBudget] = useState(0);
  const [currency, setCurrency] = useState("USD"); // Default currency

  // Event handlers for input changes
  const handleTransportationChange = (e) =>
    setTransportation(parseFloat(e.target.value) || 0);
  const handleAccommodationChange = (e) =>
    setAccommodation(parseFloat(e.target.value) || 0);
  const handleFoodChange = (e) => setFood(parseFloat(e.target.value) || 0);
  const handleActivitiesChange = (e) =>
    setActivities(parseFloat(e.target.value) || 0);
  const handleCurrencyChange = (e) => setCurrency(e.target.value);

  // useEffect to calculate total budget
  useEffect(() => {
    const budget = transportation + accommodation + food + activities;
    setTotalBudget(budget);

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [transportation, accommodation, food, activities]);

  return (
    <Layout title="Travel Budget Calculator" path="travel-budget">
      <section className="section-padding" id="travel-budget">
        <div className="container mt-5">
          <div className="row">
            {/* Travel Budget Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Travel Budget Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Currency Selector */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="currency" className="form-label">
                        Select Currency
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Select the currency you are using for your travel budget.</strong> Choose the appropriate currency for all expense categories."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <select
                        className="form-select"
                        id="currency"
                        value={currency}
                        onChange={handleCurrencyChange}
                      >
                        <option value="USD">USD (US Dollar)</option>
                        <option value="EUR">EUR (Euro)</option>
                        <option value="GBP">GBP (British Pound)</option>
                        <option value="INR">INR (Indian Rupee)</option>
                        <option value="JPY">JPY (Japanese Yen)</option>
                        <option value="CAD">CAD (Canadian Dollar)</option>
                        <option value="AUD">AUD (Australian Dollar)</option>
                        <option value="CNY">CNY (Chinese Yuan)</option>
                        <option value="SAR">SAR (Saudi Riyal)</option>
                        <option value="ZAR">ZAR (South African Rand)</option>
                      </select>
                    </div>

                    {/* Input for Transportation */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="transportation" className="form-label">
                        Transportation ({currency})
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Transportation:</strong> The total cost of all transportation during the trip, such as flights, taxis, or public transit."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="transportation"
                        placeholder={`Enter transportation cost in ${currency}`}
                        value={transportation || ""}
                        onChange={handleTransportationChange}
                      />
                    </div>

                    {/* Input for Accommodation */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="accommodation" className="form-label">
                        Accommodation ({currency})
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Accommodation:</strong> The cost of staying in hotels, hostels, or other forms of lodging during the trip."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="accommodation"
                        placeholder={`Enter accommodation cost in ${currency}`}
                        value={accommodation || ""}
                        onChange={handleAccommodationChange}
                      />
                    </div>

                    {/* Input for Food */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="food" className="form-label">
                        Food ({currency})
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Food:</strong> The total cost for food during the trip, including meals and snacks."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="food"
                        placeholder={`Enter food cost in ${currency}`}
                        value={food || ""}
                        onChange={handleFoodChange}
                      />
                    </div>

                    {/* Input for Activities */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="activities" className="form-label">
                        Activities ({currency})
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Activities:</strong> The total cost for various activities during the trip, such as tours, entertainment, and excursions."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="activities"
                        placeholder={`Enter activities cost in ${currency}`}
                        value={activities || ""}
                        onChange={handleActivitiesChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Total Travel Budget:{" "}
                        <span className="fw-bold ms-3">
                          {totalBudget === 0
                            ? `0.00 ${currency}`
                            : `${totalBudget.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })} ${currency}`}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to Travel Budget Calculation
                </h3>

                {/* What is a Travel Budget? */}
                <h4 className="mt-3 mb-3">What is a Travel Budget?</h4>
                <p>
                  A **Travel Budget** is a financial plan that outlines the expected expenses during a trip, helping travelers allocate funds for transportation, accommodation, food, and activities.
                </p>

                {/* Why Use the Travel Budget Calculator? */}
                <h4 className="mt-4 mb-3">Why Use the Travel Budget Calculator?</h4>
                <p>
                  This calculator enables travelers to anticipate costs and plan their trips effectively, ensuring they stay within their budget and make informed spending decisions.
                </p>

                {/* Benefits of Using the Travel Budget Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using the Travel Budget Calculator</h4>
                <ul>
                  <li>
                    <strong>Effective Planning:</strong> Allows travelers to allocate funds to essential categories.
                  </li>
                  <li>
                    <strong>Cost Control:</strong> Helps avoid overspending and unexpected expenses.
                  </li>
                  <li>
                    <strong>Flexibility:</strong> Enables adjustments to plans based on available budget.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use the Travel Budget Calculator?</h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Travelers:</strong> Planning solo, family, or group trips.
                  </li>
                  <li>
                    <strong>Travel Agencies:</strong> Estimating client travel packages.
                  </li>
                  <li>
                    <strong>Budget-Conscious Individuals:</strong> Ensuring a cost-effective trip experience.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h5 className="mt-3 mb-2">What are typical travel budget categories?</h5>
                <p>
                  Typical categories include transportation, accommodation, food, activities, shopping, and contingencies.
                </p>

                <h5 className="mt-3 mb-2">How can I reduce travel costs?</h5>
                <p>
                  You can save on travel costs by booking early, choosing budget-friendly accommodations, and prioritizing activities based on interest and cost.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TravelBudget;
